import { useAuth } from '../contexts/AuthContext';

/**
 * Hook pour gérer les permissions basées sur le rôle de l'utilisateur
 */
export const usePermissions = () => {
  const { user } = useAuth();

  // Définition des permissions par rôle
  const rolePermissions = {
    admin: {
      create: true,
      read: true,
      update: true,
      delete: true,
      manage: true
    },
    secretaire: {
      create: true,
      read: true,
      update: true,
      delete: false,
      manage: false
    },
    enseignant: {
      create: false,
      read: true,
      update: false,
      delete: false,
      manage: false
    },
    intendant: {
      create: false,
      read: true,
      update: false,
      delete: false,
      manage: false
    }
  };

  // Permissions par entité spécifique
  const entityPermissions = {
    // Inscriptions - seuls admin et secretaire peuvent créer/modifier
    inscriptions: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Élèves - seuls admin et secretaire peuvent créer/modifier
    eleves: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Ouvertures de classes - seuls admin peut gérer
    ouvertures: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Évaluations - enseignants peuvent créer, admin peut tout
    evaluations: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: true, update: true, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Années scolaires - seul admin peut gérer
    annees: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Classes - seul admin peut gérer
    classes: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Matières - seul admin peut gérer
    matieres: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Professeurs - admin et secretaire peuvent gérer
    professeurs: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Utilisateurs - seul admin peut gérer
    users: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Branches - admin et secretaire peuvent gérer
    branches: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Cycles - admin et secretaire peuvent gérer
    cycles: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Périodes - admin et secretaire peuvent gérer
    periodes: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Types d'évaluations - admin et secretaire peuvent gérer
    type_evaluations: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Paiements - admin et secretaire peuvent gérer
    paiements: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Incidents - admin et secretaire peuvent gérer
    incidents: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Établissement - seul admin peut gérer
    etablissement: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Rôles - seul admin peut gérer
    roles: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: false, update: false, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Religions - admin et secretaire peuvent gérer
    religions: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Professions - admin et secretaire peuvent gérer
    professions: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Types de parenté - admin et secretaire peuvent gérer
    type_parentes: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Qualifications - admin et secretaire peuvent gérer
    qualifications: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Qualifications des professeurs - admin et secretaire peuvent gérer
    prof_qualif: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    },
    // Niveaux de qualification des professeurs - admin et secretaire peuvent gérer
    niveau_qualif_prof: {
      admin: { create: true, update: true, delete: true },
      secretaire: { create: true, update: true, delete: false },
      enseignant: { create: false, update: false, delete: false },
      intendant: { create: false, update: false, delete: false }
    }
  };

  /**
   * Vérifie si l'utilisateur a une permission générale
   */
  const hasPermission = (permission) => {
    if (!user?.role) return false;
    return rolePermissions[user.role]?.[permission] || false;
  };

  /**
   * Vérifie si l'utilisateur a une permission pour une entité spécifique
   */
  const hasEntityPermission = (entity, permission) => {
    if (!user?.role) return false;
    return entityPermissions[entity]?.[user.role]?.[permission] || false;
  };

  /**
   * Vérifie si l'utilisateur peut créer des éléments
   */
  const canCreate = (entity = null) => {
    if (entity) {
      return hasEntityPermission(entity, 'create');
    }
    return hasPermission('create');
  };

  /**
   * Vérifie si l'utilisateur peut modifier des éléments
   */
  const canUpdate = (entity = null) => {
    if (entity) {
      return hasEntityPermission(entity, 'update');
    }
    return hasPermission('update');
  };

  /**
   * Vérifie si l'utilisateur peut supprimer des éléments
   */
  const canDelete = (entity = null) => {
    if (entity) {
      return hasEntityPermission(entity, 'delete');
    }
    return hasPermission('delete');
  };

  /**
   * Vérifie si l'utilisateur peut lire des éléments
   */
  const canRead = (entity = null) => {
    if (entity) {
      return hasEntityPermission(entity, 'read');
    }
    return hasPermission('read');
  };

  /**
   * Vérifie si l'utilisateur peut gérer (toutes les opérations)
   */
  const canManage = (entity = null) => {
    if (entity) {
      return hasEntityPermission(entity, 'manage');
    }
    return hasPermission('manage');
  };

  return {
    hasPermission,
    hasEntityPermission,
    canCreate,
    canUpdate,
    canDelete,
    canRead,
    canManage,
    userRole: user?.role
  };
};
